<!--
  Super Admin: the reconciliation drill-down -- 50b section 21's acceptance
  surface.

  Plan: docs/planning/2026-09-02-stripe-payments-payouts-implementation-plan.md
  section "Unit 10 -- Ops and Super Admin surfaces".
  Requirements: OPS-06.

  THIS IS NOT A REPORT. It is the acceptance test, rendered.

  The plan says OPS-06's chain IS 50b section 21's acceptance test, and that
  it is "the thing that makes the two full test-mode run-throughs checkable
  by a human". So the page is laid out AS the chain, one link per row, in the
  order money actually moves:

      order -> tender/funding -> fee + S&H collection -> decoration funding
      path -> invoice if any -> payable -> Transfer

  A person walking it top to bottom should be able to answer "did this order
  settle correctly, and if not, which link broke". That is why the links are
  rendered even when empty, with an explicit "not applicable" rather than
  being hidden: a missing row reads as a page that forgot something, while a
  row that says "no invoice -- decoration was collected at checkout" is an
  ANSWER.

  THE TWO THINGS THIS PAGE MUST NEVER SAY

  1. It must never show an approved payable as paid. `approved` means we
     authorized it; only tf-deconetwork reporting a Transfer id makes it
     paid. The template keys the Transfer row off `transfer.is_transferred`,
     which the server derives from the presence of HIS id -- never from our
     own state. The gap between the two IS the accepted liquidity exposure
     (plan section 7.1) and this surface exists partly to display it.

  2. It must never show an in-flight ACH debit as collected. The collection
     block reads `collection.is_settled`, which the server takes from Unit
     8's single reader. `initiated` and `processing` render as "in flight",
     which is what they are.

  ASCII-only.
-->
<template>
    <div data-testid="sa-reconciliation">
        <h1 class="sa-page-title">Order reconciliation</h1>

        <p class="sa-rec-intro">
            One order, end to end -- what the shopper paid, the platform fee
            and shipping it owes and where each stands, how decoration was
            funded, and what the decorator is owed. Use it to confirm an order settled correctly,
            or to find the link where it did not.
        </p>

        <form class="sa-rec-lookup" @submit.prevent="lookup">
            <label class="sa-rec-label" for="sa-rec-order-input">
                Order number
            </label>
            <input
                id="sa-rec-order-input"
                v-model="orderInput"
                class="sa-rec-input"
                data-testid="sa-reconciliation-order-input"
                placeholder="e.g. ZL-2026-01011, TF-302, or 302"
            />
            <button
                class="sa-rec-btn"
                data-testid="sa-reconciliation-lookup"
                :disabled="loading"
                type="submit"
            >
                Look up
            </button>
        </form>

        <p
            v-if="loading"
            class="sa-rec-state"
            data-testid="sa-reconciliation-loading"
        >
            Loading...
        </p>

        <p
            v-else-if="notFound"
            class="sa-rec-state"
            data-testid="sa-reconciliation-not-found"
        >
            No order with that number. Check the number and try again --
            nothing has been changed.
        </p>

        <div
            v-else-if="ambiguous"
            class="sa-rec-state"
            data-testid="sa-reconciliation-ambiguous"
        >
            <p>
                More than one store has an order with that number. Look it
                up by its TF- key instead:
            </p>
            <ul>
                <li v-for="m in ambiguous" :key="m.id">
                    {{ m.order_key }} -- {{ m.store_name || "unknown store" }}
                </li>
            </ul>
        </div>

        <p
            v-else-if="errorMsg"
            class="sa-rec-state sa-rec-state--error"
            data-testid="sa-reconciliation-error"
        >
            {{ errorMsg }}
        </p>

        <div
            v-else-if="chain"
            class="sa-rec-chain"
            data-testid="sa-reconciliation-chain"
        >
            <!-- LINK 1: the order, its tender and its funding state. -->
            <section class="sa-rec-link" data-testid="sa-rec-link-order">
                <h2 class="sa-rec-link-title">1. Order</h2>
                <dl class="sa-rec-dl">
                    <dt>Order number</dt>
                    <dd data-testid="sa-rec-order-number">
                        {{ chain.order.order_number || "--" }}
                    </dd>
                    <dt>Order key</dt>
                    <dd data-testid="sa-rec-order-key">
                        {{ chain.order.order_key }}
                    </dd>
                    <dt>Store</dt>
                    <dd>{{ chain.order.store_name || "--" }}</dd>
                    <dt>Status</dt>
                    <dd>{{ chain.order.status || "--" }}</dd>
                    <dt>Tender</dt>
                    <dd data-testid="sa-rec-tender">
                        {{ tenderLabel }}
                    </dd>
                    <dt>Funding state</dt>
                    <dd data-testid="sa-rec-funding-state">
                        {{ fundingLabel }}
                    </dd>
                    <!--
                      Net orders only: who authorized the credit (the store
                      PIN, or distributor/ThreadFlow staff). A Net order
                      from before the PIN rollout says so instead of PIN.
                    -->
                    <template v-if="chain.order.tender === 'net_checkout'">
                        <dt>Authorized by</dt>
                        <dd data-testid="sa-rec-net-authorization">
                            {{ netAuthorizationText }}
                        </dd>
                    </template>
                </dl>
            </section>

            <!--
              LINK 2: the platform fee and shipping, as the amount EXPECTED
              and where it stands. Never a defaulted $0.00 (Bug Intake
              #563): a Net order owes them on the distributor's 1st/15th
              invoice after the order ships, a card order is charged at
              batch release, and a figure the order never froze prints
              "--". Rows a tender does not have are not rendered.
            -->
            <section class="sa-rec-link" data-testid="sa-rec-link-collected">
                <h2 class="sa-rec-link-title">2. Platform fee and shipping</h2>
                <p class="sa-rec-note" data-testid="sa-rec-fee-note">
                    {{ feeNote }}
                </p>
                <dl class="sa-rec-dl">
                    <template v-for="row in feeRows" :key="row.key">
                        <dt>{{ row.label }}</dt>
                        <dd>
                            <span :data-testid="`sa-rec-${row.testid}`">
                                {{ money(row.line.cents) }}
                            </span>
                            <span
                                class="sa-rec-hint"
                                :data-testid="`sa-rec-${row.testid}-state`"
                            >
                                {{ feeStateLabel(row.line.state) }}
                            </span>
                        </dd>
                    </template>
                </dl>
            </section>

            <!-- LINK 3: how decoration was funded. THE asymmetry. -->
            <section class="sa-rec-link" data-testid="sa-rec-link-decoration">
                <h2 class="sa-rec-link-title">3. Decoration funding</h2>
                <p
                    class="sa-rec-path"
                    :class="`sa-rec-path--${chain.decoration_funding.path}`"
                    data-testid="sa-rec-decoration-path"
                >
                    {{ decorationPathLabel }}
                </p>
                <dl class="sa-rec-dl">
                    <dt>Decorator payout layer</dt>
                    <dd data-testid="sa-rec-decoration-cost">
                        {{ money(chain.decoration_funding.decoration_cost_cents) }}
                    </dd>
                    <dt>ThreadFlow charge layer</dt>
                    <dd>
                        {{ money(chain.decoration_funding.decoration_charge_cents) }}
                    </dd>
                </dl>
            </section>

            <!-- LINK 4: fulfillment -- the fact everything downstream keys off. -->
            <section class="sa-rec-link" data-testid="sa-rec-link-fulfillment">
                <h2 class="sa-rec-link-title">4. Shipment</h2>
                <template v-if="chain.fulfillment">
                    <dl class="sa-rec-dl">
                        <dt>Decorator</dt>
                        <dd>{{ chain.fulfillment.decorator_tenant_id || "--" }}</dd>
                        <dt>Reported status</dt>
                        <dd>{{ chain.fulfillment.fulfillment_status }}</dd>
                        <dt>Full shipment</dt>
                        <dd data-testid="sa-rec-full-shipment">
                            {{ chain.fulfillment.is_full_shipment ? "Yes" : "No" }}
                        </dd>
                        <dt>Reported at</dt>
                        <dd>{{ day(chain.fulfillment.reported_at) }}</dd>
                    </dl>
                </template>
                <!--
                  Stated, not hidden. "Not shipped yet" is why there is no
                  payable below, and a reader tracing a missing payable needs
                  to land here rather than on a blank space.
                -->
                <p v-else class="sa-rec-absent" data-testid="sa-rec-no-fulfillment">
                    No shipment reported yet. Nothing downstream of this point
                    exists until the decorator reports the order shipped.
                </p>
            </section>

            <!-- LINK 5: the invoice. Absent for a card order unless the order
                 bills a card share shortfall (#621): the card paid the rest. -->
            <section class="sa-rec-link" data-testid="sa-rec-link-invoice">
                <h2 class="sa-rec-link-title">5. Distributor invoice</h2>
                <template v-if="chain.invoice">
                    <dl class="sa-rec-dl">
                        <dt>Invoice</dt>
                        <dd data-testid="sa-rec-invoice-id">
                            #{{ chain.invoice.id }}
                        </dd>
                        <dt>Status</dt>
                        <dd>{{ chain.invoice.status }}</dd>
                        <dt>Billed on this invoice</dt>
                        <dd data-testid="sa-rec-invoice-amount">
                            {{ money(chain.invoice.line_amount_cents) }}
                        </dd>
                        <dt>Period</dt>
                        <dd>
                            {{ day(chain.invoice.period_start) }} to
                            {{ day(chain.invoice.period_end) }}
                        </dd>
                        <dt>Due</dt>
                        <dd>{{ day(chain.invoice.due_at) }}</dd>
                    </dl>

                    <!--
                      Collection is a SEPARATE fact from invoicing. "We
                      invoiced" is not "they paid", and an ACH debit spends
                      most of its life in between.
                    -->
                    <div
                        v-if="chain.invoice.collection"
                        class="sa-rec-collection"
                        data-testid="sa-rec-collection"
                    >
                        <span
                            class="sa-rec-badge"
                            :class="collectionBadgeClass"
                            data-testid="sa-rec-collection-state"
                        >{{ collectionLabel }}</span>
                        <span class="sa-rec-collection-amount">
                            {{ money(chain.invoice.collection.amount_cents) }}
                        </span>
                        <span
                            v-if="chain.invoice.collection.failure_message"
                            class="sa-rec-collection-failure"
                        >
                            {{ chain.invoice.collection.failure_message }}
                        </span>
                    </div>
                    <p
                        v-else
                        class="sa-rec-absent"
                        data-testid="sa-rec-no-collection"
                    >
                        No collection attempt recorded against this invoice
                        yet.
                    </p>
                </template>
                <!--
                  For a card order (not payroll) this is the CORRECT answer,
                  not a gap --
                  decoration was already paid at checkout, and invoicing it
                  again would charge the customer twice (CC-04).
                -->
                <p v-else class="sa-rec-absent" data-testid="sa-rec-no-invoice">
                    {{ noInvoiceExplanation }}
                </p>
            </section>

            <!-- LINK 6: the payable. -->
            <section class="sa-rec-link" data-testid="sa-rec-link-payable">
                <h2 class="sa-rec-link-title">6. Decorator payable</h2>
                <template v-if="chain.payable">
                    <dl class="sa-rec-dl">
                        <dt>State</dt>
                        <dd data-testid="sa-rec-payable-state">
                            {{ chain.payable.state }}
                        </dd>
                        <dt>Decoration payout</dt>
                        <dd>
                            {{ money(chain.payable.decoration_payout_cents) }}
                        </dd>
                        <dt>Handling allocation</dt>
                        <dd>
                            {{ money(chain.payable.handling_allocation_cents) }}
                            <span class="sa-rec-hint">
                                ({{ chain.payable.shipment_count }} shipment(s))
                            </span>
                        </dd>
                        <dt>Total owed</dt>
                        <dd data-testid="sa-rec-payable-total">
                            {{ money(chain.payable.total_cents) }}
                        </dd>
                    </dl>
                    <p
                        v-if="chain.payable.hold_reason"
                        class="sa-rec-hold"
                        data-testid="sa-rec-payable-hold"
                    >
                        Held: {{ chain.payable.hold_reason }}
                    </p>
                </template>
                <p v-else class="sa-rec-absent" data-testid="sa-rec-no-payable">
                    No payable yet. One is created when the decorator reports
                    the order fully shipped.
                </p>
            </section>

            <!--
              LINK 7: the Transfer. READ-THROUGH FROM tf-deconetwork.

              This is the row where getting it wrong is most expensive, so
              the wording is deliberate: an approved payable with no Transfer
              says "Awaiting transfer -- we owe this", never "Paid".
            -->
            <section class="sa-rec-link" data-testid="sa-rec-link-transfer">
                <h2 class="sa-rec-link-title">7. Transfer</h2>
                <p class="sa-rec-note">
                    Transfers are executed by tf-deconetwork, not here. This
                    reflects what they have reported back to us.
                </p>
                <template v-if="chain.transfer">
                    <p
                        class="sa-rec-badge"
                        :class="transferBadgeClass"
                        data-testid="sa-rec-transfer-state"
                    >
                        {{ transferLabel }}
                    </p>
                    <dl v-if="chain.transfer.is_transferred" class="sa-rec-dl">
                        <dt>Transfer id</dt>
                        <dd data-testid="sa-rec-transfer-id">
                            {{ chain.transfer.stripe_transfer_id }}
                        </dd>
                        <dt>Executed at</dt>
                        <dd>{{ day(chain.transfer.transferred_at) }}</dd>
                        <dt>Amount moved</dt>
                        <dd>
                            {{ money(chain.transfer.transferred_amount_cents) }}
                        </dd>
                    </dl>
                    <!--
                      A variance is SURFACED, never reconciled away. Rewriting
                      the snapshot to agree would erase the discrepancy
                      exactly when someone needs to see it.
                    -->
                    <p
                        v-if="hasVariance"
                        class="sa-rec-variance"
                        data-testid="sa-rec-transfer-variance"
                    >
                        Variance against the frozen amount:
                        {{ money(chain.transfer.variance_cents) }}
                    </p>
                    <p
                        v-if="chain.transfer.transfer_failure_reason"
                        class="sa-rec-variance"
                    >
                        {{ chain.transfer.transfer_failure_reason }}
                    </p>
                </template>
                <p v-else class="sa-rec-absent">
                    Nothing to transfer yet.
                </p>
            </section>
        </div>
    </div>
</template>

<script>
import api from "@/services/api";
import { netAuthorizationLabel } from "@/utils/netAuthorization";

function money(cents) {
    if (cents === null || cents === undefined) return "--";
    return `$${(Number(cents) / 100).toFixed(2)}`;
}

const FEE_STATE_LABELS = {
    expected_not_invoiced: "Expected, not yet invoiced",
    invoiced: "Invoiced",
    settled: "Settled",
    none_owed: "None owed",
    charged_at_batch_release: "Charged at batch release",
    recorded_at_charge: "Recorded at charge",
    not_recorded: "Not recorded on this order",
};

function feeStateLabel(state) {
    return FEE_STATE_LABELS[state] || "";
}

function day(iso) {
    if (!iso) return "--";
    return String(iso).slice(0, 10);
}

export default {
    name: "SuperAdminPaymentsReconciliation",
    data() {
        return {
            orderInput: "",
            loading: false,
            notFound: false,
            ambiguous: null,
            errorMsg: "",
            chain: null,
        };
    },
    computed: {
        netAuthorizationText() {
            return netAuthorizationLabel(this.chain?.order?.net_authorization);
        },
        tenderLabel() {
            const tender = this.chain?.order?.tender;
            if (tender === "net_checkout") return "Net Checkout";
            // A payroll order is frozen as tender "card" but no card is
            // charged; saying "Card" would hide why its decoration is
            // invoiced to the distributor.
            if (
                tender === "card" &&
                this.chain?.order?.payment_instrument === "payroll"
            ) {
                return "Payroll deduction (no card charged)";
            }
            if (tender === "card") return "Card";
            // A pre-Unit-4 order genuinely has no recorded tender. Saying so
            // is more honest than defaulting it to "Card" -- the backfill
            // was deliberately not done, and inventing the value here would
            // undo that decision in the UI.
            return "Not recorded";
        },
        fundingLabel() {
            const state = this.chain?.order?.funding_state;
            if (state === "deferred") return "Deferred -- decoration is owed";
            if (state === "collected") return "Collected";
            return "Not recorded";
        },
        preCharge() {
            return this.chain?.fee_and_shipping?.basis === "card_pre_charge";
        },
        feeNote() {
            const basis = this.chain?.fee_and_shipping?.basis;
            if (basis === "net_obligation") {
                return (
                    "Net Checkout: nothing is collected at checkout. The " +
                    "platform fee and shipping are added to the store " +
                    "owner's 1st/15th invoice after the order ships."
                );
            }
            if (basis === "payroll_obligation") {
                return (
                    "Payroll deduction: no card is charged. The platform " +
                    "fee is added to the store owner's 1st/15th invoice " +
                    "after the order ships."
                );
            }
            if (basis === "card_pre_charge") {
                return (
                    "Not charged yet. These are the amounts the order " +
                    "froze when it was placed; the card is charged when " +
                    "the batch is released."
                );
            }
            return (
                "The batch has been charged. These are the amounts " +
                "recorded in the ledger at that charge."
            );
        },
        feeRows() {
            const fs = this.chain?.fee_and_shipping;
            if (!fs) return [];
            const rows = [
                {
                    key: "platform_fee",
                    testid: "platform-fee",
                    label: "ThreadFlow platform fee",
                },
                {
                    key: "shipping_total",
                    testid: "shipping-total",
                    label: "Shipping",
                },
                {
                    key: "shipping_markup",
                    testid: "shipping-markup",
                    label: "Shipping markup",
                },
                {
                    key: "shipping_carrier",
                    testid: "shipping-carrier",
                    label: "Carrier cost",
                },
                {
                    key: "stripe_fee",
                    testid: "stripe-fee",
                    label: "Card processing fee",
                },
            ];
            return rows
                .map((row) => ({ ...row, line: fs[row.key] }))
                .filter(
                    (row) => row.line && row.line.state !== "not_applicable",
                );
        },
        decorationPathLabel() {
            const path = this.chain?.decoration_funding?.path;
            if (path === "deferred_to_invoice") {
                return "Deferred -- billed to the distributor on the 1st/15th";
            }
            if (this.preCharge) {
                return "Charged to the shopper's card at batch release";
            }
            return "Collected at checkout from the shopper";
        },
        noInvoiceExplanation() {
            // The card case is an ANSWER, not an absence. Keyed on the
            // server's decoration path, not the tender: a payroll order is
            // tender "card" and its decoration IS invoiced.
            if (
                this.chain?.decoration_funding?.path !==
                "deferred_to_invoice"
            ) {
                if (this.preCharge) {
                    return (
                        "No invoice, and that is correct -- decoration is " +
                        "charged to the shopper's card at batch release. " +
                        "Billing it to the distributor too would charge it " +
                        "twice."
                    );
                }
                return (
                    "No invoice, and that is correct -- decoration was " +
                    "collected from the shopper at checkout. Billing it " +
                    "again would charge them twice."
                );
            }
            // Trello #563: a Net order's fee and shipping ride the same line.
            // Trello #618: a payroll order's fee does too; its shipping is
            // not billed while who owes it is undecided.
            if (this.chain?.order?.tender === "net_checkout") {
                return (
                    "Not invoiced yet. This order's decoration, platform fee " +
                    "and shipping are billed to the distributor on the 1st " +
                    "and the 15th after the order ships."
                );
            }
            return (
                "Not invoiced yet. This order's decoration and platform fee " +
                "are billed to the distributor on the 1st and the 15th " +
                "after the order ships."
            );
        },
        collectionLabel() {
            const c = this.chain?.invoice?.collection;
            if (!c) return "";
            // Reads the server's is_settled, which reads Unit 8's single
            // reader. No state comparison happens in this component.
            if (c.is_settled) return "Settled";
            if (c.is_failed_or_returned) return "Failed or returned";
            // initiated / processing. NOT money.
            return "In flight -- not yet settled";
        },
        collectionBadgeClass() {
            const c = this.chain?.invoice?.collection;
            if (!c) return "";
            if (c.is_settled) return "sa-rec-badge--good";
            if (c.is_failed_or_returned) return "sa-rec-badge--bad";
            return "sa-rec-badge--pending";
        },
        transferLabel() {
            const t = this.chain?.transfer;
            if (!t) return "";
            if (t.is_transferred) return "Transferred by tf-deconetwork";
            if (t.transfer_failure_reason) return "Transfer failed";
            const state = this.chain?.payable?.state;
            if (state === "held") return "Held -- not released for settlement";
            if (state === "approved") {
                // THE SENTENCE THAT MUST NOT SAY "PAID".
                return "Approved -- awaiting transfer. We owe this.";
            }
            return "Not yet approved for transfer";
        },
        transferBadgeClass() {
            const t = this.chain?.transfer;
            if (t?.is_transferred) return "sa-rec-badge--good";
            if (t?.transfer_failure_reason) return "sa-rec-badge--bad";
            return "sa-rec-badge--pending";
        },
        hasVariance() {
            const v = this.chain?.transfer?.variance_cents;
            return v !== null && v !== undefined && v !== 0;
        },
    },
    mounted() {
        const routeId = this.$route?.params?.orderId;
        if (routeId) {
            this.orderInput = String(routeId);
            this.fetch(routeId);
        }
    },
    methods: {
        money,
        feeStateLabel,
        day,
        lookup() {
            const trimmed = String(this.orderInput || "").trim();
            if (!trimmed) return;
            // Keep the URL the source of truth so an operator can share the
            // link to a specific order with someone else.
            this.$router
                .push(
                    `/admin/payments/reconciliation/${encodeURIComponent(trimmed)}`
                )
                .catch(() => {});
            this.fetch(trimmed);
        },
        async fetch(orderId) {
            this.loading = true;
            this.notFound = false;
            this.ambiguous = null;
            this.errorMsg = "";
            this.chain = null;
            try {
                const resp = await api.get(
                    `/superadmin/payments/reconciliation/${encodeURIComponent(
                        String(orderId).trim()
                    )}`
                );
                this.chain = resp && resp.data ? resp.data : resp;
            } catch (e) {
                // A missing order is a legible empty state, not an error --
                // an operator pasting a wrong number is the most likely
                // first interaction this page ever gets.
                if (e && e.response && e.response.status === 404) {
                    this.notFound = true;
                } else if (
                    e &&
                    e.response &&
                    e.response.status === 409 &&
                    Array.isArray(e.response.data?.matches)
                ) {
                    // The same number exists in two stores: never guess.
                    this.ambiguous = e.response.data.matches;
                } else {
                    this.errorMsg =
                        "Could not load this order. Please try again.";
                }
            } finally {
                this.loading = false;
            }
        },
    },
    watch: {
        // Support navigating between orders without a remount.
        "$route.params.orderId"(next) {
            if (next) {
                this.orderInput = String(next);
                this.fetch(next);
            }
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #1a202c;
}
.sa-rec-intro {
    font-size: 14px;
    color: #4a5568;
    margin: 0 0 16px;
    max-width: 720px;
}
.sa-rec-lookup {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-bottom: 20px;
}
.sa-rec-label {
    font-size: 13px;
    color: #4a5568;
    display: block;
    margin-bottom: 4px;
}
.sa-rec-input {
    padding: 6px 10px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    font-size: 14px;
}
.sa-rec-btn {
    font-size: 13px;
    padding: 7px 14px;
    border: 1px solid #2b6cb0;
    color: #2b6cb0;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
}
.sa-rec-btn:disabled {
    opacity: 0.6;
    cursor: default;
}
.sa-rec-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-rec-state--error {
    color: #c53030;
}
.sa-rec-chain {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 720px;
}
.sa-rec-link {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;
}
.sa-rec-link-title {
    font-size: 15px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #2d3748;
}
.sa-rec-note {
    font-size: 12px;
    color: var(--tf-fg-quiet);
    margin: 0 0 8px;
}
.sa-rec-dl {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 4px 12px;
    margin: 0;
    font-size: 14px;
}
.sa-rec-dl dt {
    color: #4a5568;
}
.sa-rec-dl dd {
    margin: 0;
    color: #1a202c;
}
.sa-rec-hint {
    color: var(--tf-fg-quiet);
    font-size: 12px;
}
.sa-rec-absent {
    font-size: 13px;
    color: var(--tf-fg-quiet);
    margin: 0;
    font-style: italic;
}
.sa-rec-path {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 8px;
}
.sa-rec-path--deferred_to_invoice {
    color: #b7791f;
}
.sa-rec-path--collected_at_checkout {
    color: #2f855a;
}
.sa-rec-badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
    margin: 0 8px 8px 0;
}
.sa-rec-badge--good {
    background: #f0fff4;
    color: #2f855a;
}
.sa-rec-badge--pending {
    background: #fffaf0;
    color: #b7791f;
}
.sa-rec-badge--bad {
    background: #fff5f5;
    color: #c53030;
}
.sa-rec-collection {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    font-size: 13px;
}
.sa-rec-collection-failure {
    color: #c53030;
}
.sa-rec-variance {
    font-size: 13px;
    color: #c05621;
    margin: 8px 0 0;
}
.sa-rec-hold {
    font-size: 13px;
    color: #c05621;
    margin: 8px 0 0;
}
</style>
